import { useMemo } from "react";
import type { Rec, Finding, Mirror, Severity } from "@shared/types.js";
import { fnum, fpct } from "../lib/format.js";
import { RULE_TITLE, RULE_DESC, sevPill } from "../lib/rules.js";
import { Kpi, BarRow } from "../components/Charts.js";

const LIMIT = 300;
const SEV_ORDER: Record<Severity, number> = { high: 0, medium: 1, low: 2 };

export function Crosscheck({
  rows, findings, mirrors, sevFilter, ruleFilter,
  onSevFilter, onRuleFilter, onExport,
}: {
  rows: Rec[];
  findings: Finding[];
  mirrors: Mirror[];
  sevFilter: string;
  ruleFilter: string;
  onSevFilter: (s: string) => void;
  onRuleFilter: (r: string) => void;
  onExport: (kind: "csv" | "copy") => void;
}) {
  const scoped = useMemo(() => new Set(rows.map((r) => r.uid)), [rows]);
  const inScope = useMemo(
    () => findings.filter((f) => scoped.has(f.uid)),
    [findings, scoped],
  );
  const shown = useMemo(
    () => inScope.filter((f) =>
      (!sevFilter || f.severity === sevFilter) && (!ruleFilter || f.rule === ruleFilter)),
    [inScope, sevFilter, ruleFilter],
  );

  const counts = { high: 0, medium: 0, low: 0 };
  for (const f of inScope) counts[f.severity]++;

  const byRule = new Map<string, Finding[]>();
  for (const f of inScope) {
    const l = byRule.get(f.rule);
    if (l) l.push(f); else byRule.set(f.rule, [f]);
  }
  const ruleRows = [...byRule.entries()].sort((a, b) => {
    const d = SEV_ORDER[a[1][0]!.severity] - SEV_ORDER[b[1][0]!.severity];
    return d !== 0 ? d : b[1].length - a[1].length;
  });

  const affected = new Set(inScope.map((f) => f.uid)).size;

  /* Which sheets carry the most problems - the fix-it-first list. */
  const bySheet = useMemo(() => {
    const m = new Map<string, { high: number; medium: number; low: number; rows: Set<string> }>();
    for (const f of inScope) {
      let e = m.get(f.sheet);
      if (!e) { e = { high: 0, medium: 0, low: 0, rows: new Set() }; m.set(f.sheet, e); }
      e[f.severity]++;
      e.rows.add(f.uid);
    }
    const totalRowsBySheet = new Map<string, number>();
    for (const r of rows) totalRowsBySheet.set(r.sheet, (totalRowsBySheet.get(r.sheet) ?? 0) + 1);
    return [...m.entries()]
      .map(([sheet, e]) => ({
        sheet, ...e,
        affected: e.rows.size,
        total: totalRowsBySheet.get(sheet) ?? 0,
        findings: e.high + e.medium + e.low,
      }))
      .sort((a, b) => b.high - a.high || b.findings - a.findings);
  }, [inScope, rows]);

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section 7</span>
          <h2><i className="bi bi-shield-check hico" aria-hidden="true" />Crosscheck &amp; data integrity</h2>
          <span className="note">
            Masterfile vs. entered data &middot; {fnum(inScope.length)} findings on the records in scope
          </span>
        </div>
        <div className="grid g-kpi">
          <Kpi icon="bi-clipboard-data" label="Total findings" value={fnum(inScope.length)} foot="Across all checks" />
          <Kpi icon="bi-exclamation-octagon" label="High severity" value={fnum(counts.high)} tone="alert" foot="Contradictions & duplicates" />
          <Kpi icon="bi-exclamation-triangle" label="Medium severity" value={fnum(counts.medium)} tone="await" foot="Missing or inconsistent fields" />
          <Kpi icon="bi-info-circle" label="Low severity" value={fnum(counts.low)} tone="none" foot="Housekeeping" />
          <Kpi icon="bi-file-earmark-text" label="Records affected" value={fnum(affected)}
            foot={<><span className="pct">{fpct(affected, rows.length)}</span> of rows in scope</>} />
        </div>
      </div>

      <div className="sec">
        <div className="sec-head">
          <h2><i className="bi bi-exclamation-triangle hico" aria-hidden="true" />Where the problems sit</h2>
          <span className="note">Sheets ranked by high-severity findings &mdash; fix these first</span>
        </div>
        <div className="card">
          <div className="bars">
            {bySheet.length === 0 && <div className="empty">No findings in scope.</div>}
            {bySheet.map((s) => (
              <BarRow
                key={s.sheet}
                label={s.sheet}
                sub={`${fnum(s.affected)} of ${fnum(s.total)} rows affected`}
                totalTxt={<><b>{fnum(s.findings)}</b> findings</>}
                segments={[
                  { label: "High", value: s.high, color: "var(--sev-high)" },
                  { label: "Medium", value: s.medium, color: "var(--sev-med)" },
                  { label: "Low", value: s.low, color: "var(--sev-low)" },
                ]}
              />
            ))}
          </div>
          <div className="tblnote">
            <span className="pill p-high">high</span> <span className="pill p-med">medium</span>{" "}
            <span className="pill p-low">low</span> &mdash; a row can carry more than one finding,
            so findings exceed affected rows.
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-arrow-left-right hico" aria-hidden="true" />Masterfile sheet reconciliation</h3>
            <span className="sub">
              Duplicate &ldquo;Details&hellip;&rdquo; sheets compared against the sheets the Overview tab actually counts
            </span>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Counted sheet</th><th>Duplicate sheet</th>
                  <th className="n">Rows</th><th className="n">Rows (dup)</th>
                  <th className="n">Priced</th><th className="n">Priced (dup)</th>
                  <th>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {mirrors.map((m) => (
                  <tr key={m.mirrorSheet}>
                    <td className="strong">{m.primarySheet}</td>
                    <td>{m.mirrorSheet}</td>
                    <td className="n">{fnum(m.primaryRows)}</td>
                    <td className="n">{fnum(m.mirrorRows)}</td>
                    <td className="n">{fnum(m.primaryDone)}</td>
                    <td className="n">{fnum(m.mirrorDone)}</td>
                    <td>
                      {m.identical ? (
                        <span className="pill p-done">identical copy</span>
                      ) : (
                        <>
                          <span className="pill p-high">out of sync</span>{" "}
                          {fnum(m.onlyInMirror.length)} code(s) only in the duplicate,{" "}
                          {fnum(m.onlyInPrimary.length)} only in the counted sheet
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="tblnote">
            The workbook keeps a second copy of several sheets. Only the first column is counted
            anywhere in this dashboard (matching the Overview tab&rsquo;s formulas). Where a copy
            is out of sync, the two sheets disagree about the same suppliers and one of them is stale.
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-ui-checks hico" aria-hidden="true" />Findings by check</h3>
            <span className="sub">Click a row to filter the list below</span>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Check</th><th>What it means</th>
                  <th className="n">Records</th><th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {ruleRows.length === 0 && (
                  <tr>
                    <td colSpan={4}><div className="empty">No findings for the current filters.</div></td>
                  </tr>
                )}
                {ruleRows.map(([rule, arr]) => {
                  const sev = arr[0]!.severity;
                  const active = ruleFilter === rule;
                  return (
                    <tr
                      key={rule} className="rulerow" style={{ cursor: "pointer" }}
                      aria-selected={active || undefined}
                      onClick={() => onRuleFilter(active ? "" : rule)}
                    >
                      <td className="strong">
                        {RULE_TITLE[rule] ?? rule}
                        {active && <> <span className="pill p-done">filtered</span></>}
                      </td>
                      <td>{RULE_DESC[rule] ?? ""}</td>
                      <td className="n">{fnum(arr.length)}</td>
                      <td><span className={`pill ${sevPill(sev)}`}>{sev}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-search hico" aria-hidden="true" />Findings detail</h3>
            <div style={{
              display: "flex", gap: 7, alignItems: "center",
              marginLeft: "auto", flexWrap: "wrap",
            }}>
              <div className="seg" role="group" aria-label="Severity">
                {["", "high", "medium", "low"].map((s) => (
                  <button
                    key={s || "all"} type="button"
                    aria-pressed={sevFilter === s}
                    onClick={() => onSevFilter(s)}
                  >
                    {s === "" ? "All" : s}
                  </button>
                ))}
              </div>
              {ruleFilter && (
                <button className="btn btn--sm" type="button" onClick={() => onRuleFilter("")}>
                  Clear check filter
                </button>
              )}
              <button className="btn btn--sm btn--primary" type="button" onClick={() => onExport("csv")}>
                Download CSV
              </button>
              <button className="btn btn--sm" type="button" onClick={() => onExport("copy")}>
                Copy for Excel
              </button>
            </div>
          </div>
          {shown.length === 0 ? (
            <div className="empty">No findings match the current filters.</div>
          ) : (
            <>
              {shown.slice(0, LIMIT).map((f, i) => (
                <div className={`finding sev-${f.severity}`} key={`${f.uid}-${f.rule}-${i}`}>
                  <span className="stripe" />
                  <div>
                    <div className="who">
                      {f.name || "(no name)"}
                      <span className="meta">
                        {f.compLabel} &middot; {f.sheet} row {f.excelRow}
                        {f.code ? ` · code ${f.code}` : ""}
                        {f.cat ? ` · cat ${f.cat}` : ""}
                      </span>
                    </div>
                    <div className="msg">{f.message}</div>
                    <span className="rulechip">{f.rule}</span>
                  </div>
                </div>
              ))}
              {shown.length > LIMIT && (
                <div className="tblnote">
                  Showing the first {LIMIT} of {fnum(shown.length)} findings &mdash; narrow the
                  filters, or export the full list.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
